import { useEffect, useState, useContext } from "react";
import { Button } from "@mui/material";
import InputText from "../../../shared/components/inputs/InputText/InputText";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import "../categoriasFinanceiro/CategoriasFinanceiro.scss";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { Popconfirm } from "antd";
import ErrorIcon from "@mui/icons-material/Error";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotaPartilhaUnidades from "../components/NotaPartilhaUnidades";

function Caixas({ sendRequest, clinicaId }) {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nomeCaixa, setNomeCaixa] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [idCaixa, setIdCaixa] = useState(null);
  const [caixas, setCaixas] = useState([]);
  const [mensagemDeErro, setMensagemDeErro] = useState("");
  const auth = useContext(AuthContext);
  const { clinica } = useContext(ClinicaContext);

  // A unidade da caixa é o que decide sob que LED saem as faturas pagas nela.
  // Só se mostra em clínicas com mais do que uma unidade.
  // `clinica` aqui JÁ É a clínica — o contexto é desestruturado acima. Com
  // `clinica?.clinica` isto dava sempre falso: o ecrã não carregava as
  // unidades, não mostrava a coluna, e o formulário de criar caixa não
  // oferecia onde a pôr.
  const multiUnidade = !!clinica?.multiEstabelecimento;

  useEffect(() => {
    const fetchCaixas = async () => {
      startLoading();
      try {
        // Sem `?todas=true`: este ecrã mostra apenas as caixas da unidade onde
        // se está a trabalhar. Gerir aqui as caixas do outro local só servia
        // para as trocar por engano — e é isso que torna desnecessário pedir a
        // unidade ao criar ou editar: é sempre a da sessão.
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinicaId}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        setCaixas(responseData.caixas);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    fetchCaixas();
  }, [auth.token, clinicaId, sendRequest]);

  // A lista de unidades deixou de ser carregada: não há onde escolher uma.

  const caixaChangeHandler = (valor) => {
    setNomeCaixa(valor);
  };

  const guardarCaixa = async () => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("caixa", nomeCaixa);
      formData.append("clinica", clinicaId);
      formData.append("criadoPor", auth.userId);
      // A unidade não vai no pedido: o servidor usa a da sessão.

      const caixaCriada = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setCaixas((current) => [...current, caixaCriada.caixa]);

      setNomeCaixa("");
      setOpenModal(false);
      setIdCaixa(null);

      toast.success("Caixa criada com sucesso");
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
    setIsSubmitting(false);
  };

  const editarCaixa = async (id) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("caixa", nomeCaixa);
      formData.append("clinica", clinicaId);
      formData.append("atualizadoPor", auth.userId);
      // A unidade NÃO se envia na edição. Antes ia sempre, e com o seletor
      // removido iria vazia — apagando a unidade da caixa e mandando as
      // faturas seguintes pelo LED errado. Uma caixa não muda de sítio: se
      // pertencer ao outro local, edita-se lá.

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/${id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );
      const caixaCopy = [...caixas];

      const caixasModificadas = caixaCopy.map((cat) => {
        if (cat.id === id) {
          // Preservar o resto do objeto: substituí-lo por { id, caixa }
          // deitava fora todos os outros campos até ao próximo carregamento.
          // A unidade fica como está — a edição não lhe toca.
          return {
            ...cat,
            caixa: nomeCaixa,
          };
        } else {
          return cat;
        }
      });

      setCaixas([...caixasModificadas]);

      setNomeCaixa("");
      setIdCaixa(null);
      setOpenModal(false);

      toast.success("Caixa modificada com sucesso");
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
    setIsSubmitting(false);
  };

  const preEditarCaixa = async (id) => {
    const caixaAEditar = caixas.filter((cat) => cat.id === id)[0];
    setIdCaixa(id);
    setNomeCaixa(caixaAEditar.caixa);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setMensagemDeErro("");
    setIdCaixa(null);
    setNomeCaixa("");
    setOpenModal(false);
  };

  const desativarCaixa = async (id) => {
    startLoading();
    try {
      const formDataDesativar = new FormData();
      formDataDesativar.append("utilizador", auth.userId);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/toggleativo/${id}`,
        "PATCH",
        formDataDesativar,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setCaixas((current) => current.filter((cat) => cat.id !== id));

      toast.success("Caixa desativada com sucesso");
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const beforeSubmit = (idCaixa) => {
    let filteredCaixa;
    let duplicidadeCaixa = false;
    if (idCaixa) {
      filteredCaixa = caixas.find((c) => c.id === idCaixa);
      // Nomes repetidos entre unidades são legítimos: cada unidade tem a sua
      // "Caixa Principal". Já não é preciso comparar a unidade aqui — a lista
      // só traz as da unidade da sessão, portanto qualquer nome repetido nela
      // é repetido dentro da mesma unidade.
      duplicidadeCaixa = caixas.filter(
        (c) =>
          c.id !== idCaixa &&
          c.caixa.toLowerCase() === nomeCaixa.trim().toLowerCase()
      );
    }

    //Se o nome permanecer igual, não modificamos nada
    if (
      idCaixa &&
      filteredCaixa?.caixa?.toLowerCase() === nomeCaixa.trim().toLowerCase()
    ) {
      setNomeCaixa("");
      setIdCaixa(null);
      setOpenModal(false);
    } else {
      let haErro = false;
      if (duplicidadeCaixa.length > 0) {
        setMensagemDeErro("Já existe uma caixa com esse nome.");
        haErro = true;
      } else if (!nomeCaixa || nomeCaixa.trim() === "") {
        setMensagemDeErro("Introduza um nome para a caixa.");
        haErro = true;
      } else {
        setMensagemDeErro("");
      }
      if (!isSubmitting && !haErro) {
        setIsSubmitting(true);
        if (idCaixa) {
          editarCaixa(idCaixa);
        } else {
          guardarCaixa();
        }
      }
    }
  };

  return (
    <>
      <div className="categoriasGeral__container">
        {(auth.role === "Médico/Administrador" || auth.role === "Gerente") && (
          <div className="categoriasGeral__container__btn-adicionar">
            <Button
              variant="contained"
              color="success"
              onClick={(e) => setOpenModal(true)}
            >
              Nova Caixa
            </Button>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            gap: "15px",
            alignItems: "flex-start",
            gridColumn: "1/-1",
          }}
        >
          <InfoOutlinedIcon style={{ color: "#1976d2", marginTop: "2px" }} />
          <div
            style={{ fontSize: "14px", color: "#1565c0", lineHeight: "1.6" }}
          >
            <strong
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "15px",
              }}
            >
              Como funcionam as Caixas?
            </strong>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              <li>
                Caixas representam os locais onde o dinheiro entra e sai (ex:
                contas bancárias, caixa física da clínica).
              </li>
              <li>
                Na área de Financeiro, pode <strong>filtrar o saldo</strong> de
                cada caixa e registar transferências entre elas, na aba
                depósitos da página financeiro.
              </li>
              <li>
                Ao registar entradas ou saídas, deve sempre indicar a caixa
                correspondente.
              </li>
              <NotaPartilhaUnidades porUnidade />
              {multiUnidade && (
                <li>
                  As faturas dos pagamentos recebidos numa caixa saem pelo{" "}
                  <strong>LED da unidade a que ela pertence</strong>.
                </li>
              )}
            </ul>
          </div>
        </div>

        {caixas &&
          caixas.map((caixa, index) => (
            <div
              className="categoriasFinanceiro__container__categoria"
              key={index}
            >
              <div className="categoriasFinanceiro__container__categoria--1">
                {caixa.caixa}
              </div>
              <div className="categoriasFinanceiro__container__categoria--2">
                {(auth.role === "Médico/Administrador" ||
                  auth.role === "Gerente") &&
                  caixa.caixa.toLowerCase() !== "clínica" && (
                    <Popconfirm
                      title={`Desativar Caixa`}
                      description={`Pretende desativar essa caixa?`}
                      icon={<ErrorIcon style={{ color: "red" }} />}
                      okText="Sim"
                      cancelText="Não"
                      onConfirm={desativarCaixa.bind(null, caixa.id)}
                      className="popConfirm--biggerPadding"
                    >
                      <span>Desativar</span>
                    </Popconfirm>
                  )}
                {(auth.role === "Médico/Administrador" ||
                  auth.role === "Gerente") &&
                  caixa.caixa.toLowerCase() !== "clínica" && (
                    <span onClick={preEditarCaixa.bind(null, caixa.id)}>
                      Editar
                    </span>
                  )}
              </div>
            </div>
          ))}

        <Modal
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            className="fluxo-atendimento__modal__box adicionar-categoria__modal"
            id="modal__adicionar-categoria"
          >
            <h1 className="fluxo-atendimento__modal__titulo">Caixa</h1>
            <div className="adicionar-categoria__modal__container">
              <InputText
                className="adicionar-categoria__modal__container--span2"
                label="Nome da Caixa *"
                initialValue={nomeCaixa}
                handleChange={caixaChangeHandler}
              />

              {/* Não se pergunta a unidade: a caixa nasce na unidade onde se
                  está a trabalhar, que é a única que este ecrã mostra. */}
            </div>

            {mensagemDeErro && (
              <div className="erroContainer__categoria-caixa">
                {mensagemDeErro}
              </div>
            )}

            <div className="fluxo-atendimento__modal__bottom_btns">
              <span
                className="cancel-btn"
                onClick={handleModalClose}
                style={{ display: "block" }}
              >
                Fechar
              </span>

              <span
                disabled={isSubmitting}
                className="blue-button"
                onClick={
                  idCaixa
                    ? beforeSubmit.bind(null, idCaixa)
                    : beforeSubmit.bind(null, null)
                }
              >
                {isSubmitting ? "Guardando" : "Guardar"}
              </span>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

export default Caixas;
